import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { AnyAction } from "redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Table from "../../../core/common/dataTable/index";
import { getIdCardData } from "../../../core/data/redux/idCardSlice";
import { formatEmpIdForDisplay } from "../../../utils/empIdUtils";
import { all_routes } from "../../router/all_routes";

type AppDispatch = ThunkDispatch<any, any, AnyAction>;

const IdCardList = () => {
  const routes = all_routes;
  const dispatch = useDispatch<AppDispatch>();
  const idCardState = useSelector((state: any) => state.idCardState);

  const [dataList, setDataList] = useState<any[]>([]);
  const [query, setQuery] = useState<string>("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 10,
  });

  const fetchRecords = (pagination: any, query: string) => {
    dispatch(
      getIdCardData({
        page: pagination.current,
        limit: pagination.pageSize,
        query,
      })
    );
  };

  const handleRefresh = () => {
    fetchRecords(pagination, query);
  };

  const handlePagination = (newPagination: any, filters?: any, sorter?: any) => {
    setPagination((prev) => {
      const isPageSizeChanged = prev.pageSize !== newPagination.pageSize;
      const updatedPagination = {
        current: isPageSizeChanged ? 1 : newPagination.current || 1,
        pageSize: newPagination.pageSize,
        total: newPagination.total || prev.total,
      };
      fetchRecords(updatedPagination, query);
      return updatedPagination;
    });
  };

  const handleSearch = (searchQuery: string) => {
    setQuery(searchQuery);
    const resetPagination = {
      ...pagination,
      current: 1,
    };
    setPagination(resetPagination);
    fetchRecords(resetPagination, searchQuery);
  };

  useEffect(() => {
    fetchRecords(pagination, query);
  }, []);

  useEffect(() => {
    if (
      idCardState.loading === "fulfilled" &&
      idCardState.action === "list"
    ) {
      // Handle nested data structure: 
      // idCardState.data = { status, message, data: { data: [], total, page, limit, totalPages } }
      // idCardState.data.data = { data: [], total, page, limit, totalPages }
      // idCardState.data.data.data = array of items
      const result = idCardState?.data?.data || null;

      if (result) {
        const dataArray = result?.data || [];
        setDataList(Array.isArray(dataArray) ? dataArray : []);
        setPagination({
          ...pagination,
          total: result.total || 0,
          current: result.page || 1,
          pageSize: result.limit || 10,
        });
      } else {
        setDataList([]);
      }
    } else if (idCardState.loading === "rejected") {
      toast.error(idCardState?.error || "Failed to fetch ID card requests");
    }
  }, [idCardState.loading]);

  const columns = [
    {
      title: "Sr. No.",
      render: (_: any, __: any, index: number) =>
        index + 1 + (pagination.current - 1) * pagination.pageSize,
    },
    {
      title: "EmpId",
      dataIndex: "EmpID",
      render: (EmpID: string) => <span>{formatEmpIdForDisplay(EmpID || "")}</span>,
    },
    {
      title: "Employee Name",
      dataIndex: "EmployeeName",
      render: (text: string) => <span>{text || "-"}</span>,
    },
    {
      title: "Reference Number",
      dataIndex: "ReferenceNumber",
      render: (text: string) => <span>{text || "-"}</span>,
    },
    {
      title: "Designation",
      dataIndex: "Designation",
      render: (text: string) => <span>{text || "-"}</span>,
    },
    {
      title: "Date Of Joining",
      dataIndex: "DateOfJoining",
      render: (text: string) =>
        text ? new Date(text).toLocaleDateString() : "N/A",
    },
    {
      title: "Place Of Posting",
      dataIndex: "PlaceOfPosting",
      render: (text: string) => <span>{text || "-"}</span>,
    },
    {
      title: "Request Status",
      dataIndex: "RequestStatus",
      render: (text: string) => <span>{text || "-"}</span>,
    },
    {
      title: "Approver User ID",
      dataIndex: "ApproverUserID",
      render: (text: string) => <span>{formatEmpIdForDisplay(text || "")}</span>,
    },
    {
      title: "Request Creation Date",
      dataIndex: "RequestCreationDate",
      render: (text: string) =>
        text ? new Date(text).toLocaleString() : "N/A",
    },
  ];

  return (
    <>
      <style>
        {`
          .spin {
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Id Card</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Services</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Id Card
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="mb-2">
                <button
                  className="btn btn-primary d-flex align-items-center"
                  onClick={handleRefresh}
                  disabled={idCardState.loading === "pending"}
                  title="Refresh"
                >
                  <i
                    className={`ti ti-refresh me-2 ${idCardState.loading === "pending" ? "spin" : ""
                      }`}
                  />
                  {idCardState.loading === "pending" ? "Refreshing..." : "Refresh"}
                </button>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-4 mt-3">Id Card</h4>
            </div>
            <div className="card-body p-0 py-3">
              <Table
                columns={columns}
                dataSource={dataList}
                Selection={false}
                paginationConfig={pagination}
                handlePagination={handlePagination}
                loading={
                  idCardState.loading === "pending" && idCardState.action === "list"
                }
                handleSearch={handleSearch}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default IdCardList;

